<script setup lang="ts">
import { useAddToCartFlow } from "~~/app/composables/useAddToCartFlow";
import {
  useCarrierHoursQuery,
  useHighlightedPromoCoupon,
  useNavQuery,
  useProductQuery,
  useProductQuickViewQuery,
  useProductRecommendationsQuery,
  useShopReviewsQuery,
  useToggleWishlistMutation,
  useWishlistQuery,
} from "~~/app/composables/queries/useStoreApi";
import type {
  NavItem,
  Product,
  ProductCombinationOptionGroup,
} from "~~/shared/types/store";
import InpostPayButton from "~~/app/components/inpostpay/InpostPayButton.vue";
import {
  resolveSizeVariant,
  withCurrentVariantMarked,
} from "~~/app/utils/sizeVariants";
import {
  combinationLabel,
  findSizeGroup,
  resolveSelectedCombination,
  type SelectedCombination,
} from "~~/app/utils/productCombination";
import {
  isSizeGroup,
  resolveQuoteVariant,
  resolveVariantSelection,
  type VariantPicks,
} from "~~/app/utils/quoteVariant";
import { CATALOG_HOW_IT_WORKS_STEPS } from "~~/app/utils/catalogHowItWorks";
import { pickedColorIds, variantGallery } from "~~/app/utils/variantGallery";

function findCategoryHref(items: NavItem[], categoryId: string): string | null {
  return findNavPath(items, categoryId)?.at(-1)?.href ?? null;
}

/** Ścieżka w menu od działu do kategorii (jak breadcrumb strony kategorii). */
function findNavPath(
  items: NavItem[],
  categoryId: string,
  path: NavItem[] = [],
): NavItem[] | null {
  for (const item of items) {
    const lastSeg = item.href.split("/").filter(Boolean).at(-1) ?? "";
    if (lastSeg.startsWith(`${categoryId}-`)) return [...path, item];
    if (item.children.length) {
      const found = findNavPath(item.children, categoryId, [...path, item]);
      if (found) return found;
    }
  }
  return null;
}

const route = useRoute();
const { formatPrice } = useMoney();
const slug = computed(() => String(route.params.slug));
const routeProductPath = computed(() => `/products/${slug.value}`);
const isNuxtPayloadRequest = import.meta.server
  ? useRequestURL().pathname.endsWith("/_payload.json")
  : false;
const { pendingProductPath, clearPendingProductNavigation } =
  useInstantProductNavigation();

const productQuery = useProductQuery(slug, {
  ssr: !isNuxtPayloadRequest,
});
const { data } = productQuery;

// Produktu nie ma → prawdziwe 404, a nie karta z HTTP 200, pustym <title>
// i „Nie udało się załadować produktu" (soft 404 — wyszukiwarka indeksowała
// martwe adresy). WYŁĄCZNIE koperta modułu `not_found`, którą proxy oznacza
// `data.notFound`: gołe 404 (HTML z ingressu przy zepsutym routingu), 5xx
// i błąd sieci zostają na stanie błędu karty, żeby awaria nie udawała braku
// produktu — ta sama pułapka maskowała awarie jako 404 na kategoriach.
const productNotFound = computed(() => {
  // `data` błędu $fetch = ciało odpowiedzi H3 ({ statusCode, data, … }).
  const error = productQuery.error?.value as
    | { statusCode?: number; status?: number; data?: { data?: { notFound?: unknown } } }
    | null
    | undefined;
  return (
    (error?.statusCode ?? error?.status) === 404 &&
    error?.data?.data?.notFound === true
  );
});
const productNotFoundError = () =>
  createError({
    statusCode: 404,
    statusMessage: "Produkt nie istnieje",
    fatal: true,
  });
if (import.meta.server && !isNuxtPayloadRequest) {
  // Po rozstrzygnięciu zapytania (useServerQuery prefetchuje równolegle) —
  // wyjątek z serverPrefetch kończy render stroną błędu z kodem 404.
  onServerPrefetch(async () => {
    await productQuery.suspense?.().catch(() => {});
    if (productNotFound.value) throw productNotFoundError();
  });
}
if (import.meta.client) {
  watch(
    productNotFound,
    (notFound) => {
      if (notFound) showError(productNotFoundError());
    },
    { immediate: true },
  );
}
const { data: wishlist } = useWishlistQuery();
const { data: nav } = useNavQuery();
const addToCartFlow = useAddToCartFlow();
// Tryb katalogowy: zamiast zakupu — zapytanie ofertowe. CTA zostaje linkiem
// (działa bez JS), a kliknięcie otwiera modal z formularzem.
const { sellingEnabled, ctaLabel } = useCatalogOnly();
const { open: openQuote } = useQuoteRequest();
const toggleWishlist = useToggleWishlistMutation();
const recentlyViewed = useRecentlyViewedProducts();
const analytics = useAnalytics();
const highlightedCoupon = useHighlightedPromoCoupon();
// Do bloku .pdp__notices (kod + countdown) NIE używamy stałych kodów akcji progowej
// (lavmag EXTRA*, youlook MORE*) — mają własny blok PromoStandingCodes, a ich data
// końca jest odległa (dawałoby np. „1824d"). Rozpoznanie po konfiguracji marki, nie
// po hardkodzie — inaczej kody youlooka wyciekłyby do notices.
// Notices pokazujemy tylko dla „zwykłych" kuponów / promocji produktu.
const { isStandingCode } = useStandingPromo();
const noticeCoupon = computed(() => {
  const c = highlightedCoupon.value;
  if (c && isStandingCode(c.code)) return null;
  return c;
});
// Tryb katalogowy nie wysyła paczek — „U Ciebie za 2-3 dni" przy „Zapytaj
// o wycenę" byłoby obietnicą terminu, której sklep nie składa.
const { data: carrierHours } = useCarrierHoursQuery({ enabled: sellingEnabled });
const carrierHoursText = computed(() =>
  sellingEnabled ? (carrierHours.value?.text?.trim() ?? "") : "",
);

function productMatchesRoute(current: Product) {
  const routeSlug = slug.value;
  return (
    routeSlug === current.slug || routeSlug === `${current.id}-${current.slug}`
  );
}

const isWaitingForAnotherProduct = computed(() => {
  return (
    import.meta.client &&
    Boolean(pendingProductPath.value) &&
    pendingProductPath.value !== routeProductPath.value
  );
});
const baseProduct = computed(() => {
  const current = data.value?.product;
  if (!current || isWaitingForAnotherProduct.value) return undefined;
  if (pendingProductPath.value && !productMatchesRoute(current))
    return undefined;
  return current;
});

const localSelectedSize = ref("");

// Selected allegrohash sibling — fetched on demand, null = current product
const selectedVariantSlug = ref<string | null>(null);
const { data: variantData } = useProductQuickViewQuery(selectedVariantSlug);

// Overlay dynamic fields (price, stock) from sibling product without navigating
const product = computed(() => {
  const base = baseProduct.value;
  if (!base) return undefined;

  // Patch isCurrent to reflect user selection. Bez wyboru użytkownika opieramy się
  // na fladze backendu, a gdy ta jest pusta — odtwarzamy ją po id produktu
  // (zob. withCurrentVariantMarked: regresja is_current w contelizerrest).
  const sizeVariants = !localSelectedSize.value
    ? withCurrentVariantMarked(base.sizeVariants, base.id)
    : base.sizeVariants.map((v) => ({
        ...v,
        isCurrent: v.rozmiar === localSelectedSize.value,
      }));

  const sibling = variantData.value?.product;
  if (!sibling || !selectedVariantSlug.value) return { ...base, sizeVariants };

  return {
    ...base,
    sizeVariants,
    price: sibling.price,
    compareAtPrice: sibling.compareAtPrice,
    lowestRecentPrice: sibling.lowestRecentPrice,
    quantity: sibling.quantity,
    allowOutOfStock: sibling.allowOutOfStock,
    promoEndDate: sibling.promoEndDate,
    // Wybrany rozmiar to osobny produkt-rodzeństwo (allegrohash) — opis,
    // szczegóły produktu i skład są per-wariant i muszą podążać za wyborem.
    description: sibling.description,
    productInfo: sibling.productInfo,
  };
});
const inpostpayProductId = computed<string | undefined>(() => {
  if (selectedVariantSlug.value && variantData.value?.product?.id) {
    return variantData.value.product.id;
  }
  return product.value?.id;
});

const isProductLoading = computed(() => {
  if (isWaitingForAnotherProduct.value) return true;
  if (product.value) return false;
  const hasQueryStatus =
    productQuery.isPending !== undefined ||
    productQuery.isFetching !== undefined;
  return (
    (productQuery.isPending?.value ?? false) ||
    (productQuery.isFetching?.value ?? false) ||
    !hasQueryStatus
  );
});

watch(
  [product, routeProductPath],
  ([current, currentPath]) => {
    if (current && pendingProductPath.value === currentPath) {
      clearPendingProductNavigation();
    }
  },
  { immediate: true },
);
const categoryHref = computed(() => {
  const p = product.value;
  if (!p) return null;
  if (p.categoryPath) return p.categoryPath;
  if (!p.categoryId || !p.categorySlug) return null;
  return (
    findCategoryHref(nav.value?.items ?? [], p.categoryId) ??
    `/${p.categoryId}-${p.categorySlug}`
  );
});

let _lastTrackedProductId = "";
watch(
  product,
  (current) => {
    if (!current) return;
    recentlyViewed.record(current);
    if (import.meta.client && current.id !== _lastTrackedProductId) {
      _lastTrackedProductId = current.id;
      analytics.trackViewItem(current);
    }
  },
  { immediate: true },
);

// Rekomendacje ładowane lazy po wyrenderowaniu produktu (nie blokują SSR)
const productId = computed(() => product.value?.id ?? "");
const categoryId = computed(() => product.value?.categoryId ?? "");
const { data: recsData, isPending: isRecommendationsPending } =
  useProductRecommendationsQuery(productId, categoryId);
const othersAlsoViewed = computed(
  () => recsData.value?.othersAlsoViewed?.slice(0, 12) ?? [],
);
const bestInCategory = computed(
  () => recsData.value?.bestInCategory?.slice(0, 8) ?? [],
);
const isFallback = computed(() => recsData.value?.isFallback ?? false);

// Ocena sklepu (Zaufane.pl/eKomi) — zasila gwiazdki na PDP + sekcję opinii
const { data: shopReviews } = useShopReviewsQuery();

const openAccordion = ref<string | null>(null);
const introAnimationsEnabled = ref(true);

// Getter w setupie, nie watchEffect — na serwerze efekt wykonuje się raz, przed
// danymi produktu, i HTML dostawał pusty <title> (patrz useStorefrontSeo).
useStorefrontSeo(() =>
  product.value
    ? {
        title: product.value.seoTitle,
        description: product.value.seoDescription,
        image: product.value.thumbnail,
        // Sama ścieżka produktu: `?rozmiar=` to wariant tej samej karty,
        // a parametry kampanii nie mogą tworzyć osobnego adresu kanonicznego.
        canonicalPath: routeProductPath.value,
      }
    : null,
);

// Natywne kombinacje PrestaShop (jeden produkt, rozmiar jako atrybut) — w
// odróżnieniu od serii allegrohash, gdzie każdy rozmiar to osobny produkt.
const hasNativeCombinations = computed(
  () =>
    !!product.value &&
    (product.value.sizeVariants?.length ?? 0) === 0 &&
    (product.value.combinations?.length ?? 0) > 0 &&
    !!findSizeGroup(product.value.combinationOptions),
);

// Rozmiar z adresu (`?rozmiar=L`) — link do wybranego wariantu otwiera kartę
// z tym wariantem, także w SSR. Tylko dla natywnych kombinacji i tylko gdy
// rozmiar istnieje i jest dostępny; inaczej zostaje rozmiar domyślny.
const sizeFromUrl = computed(() => {
  const raw = route.query?.rozmiar;
  const wanted = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (!wanted || !hasNativeCombinations.value || !product.value) return "";
  return (
    (product.value.sizes ?? []).find(
      (s) => s.available && s.label.trim().toLowerCase() === wanted,
    )?.label ?? ""
  );
});

const selectedSize = computed(() => {
  if (!product.value) return "";
  if (product.value.sizeVariants.length > 0) {
    return (
      localSelectedSize.value ||
      (product.value.sizeVariants.find((v) => v.isCurrent)?.rozmiar ?? "")
    );
  }
  return (
    localSelectedSize.value || sizeFromUrl.value || product.value.defaultSize
  );
});

// Sklep katalogowy: klient wybiera KAŻDĄ grupę atrybutów, nie tylko rozmiar.
// Kombinacje z EasyItem niosą rozmiar i kolor (większość produktów — sam
// kolor), a karta pokazywała wyłącznie rozmiar: kolor był zawsze domyślny,
// a produkt z samym kolorem szedł do wyceny bez kombinacji. Wybór jak
// w szybkim podglądzie (app/utils/quoteVariant). Sklepy sprzedające bez zmian —
// tam kolor dobiera resolveSelectedCombination według stanu.
const hasQuoteVariants = computed(
  () =>
    !sellingEnabled &&
    !!product.value &&
    (product.value.sizeVariants?.length ?? 0) === 0 &&
    (product.value.combinationOptions?.length ?? 0) > 0 &&
    (product.value.combinations?.length ?? 0) > 0,
);

// Grupy z przyciskami pod rozmiarem. Rozmiar ma już ProductSizeSelector —
// chyba że produkt nie ma listy rozmiarów, wtedy wybiera się go tutaj.
const quoteVariantGroups = computed<ProductCombinationOptionGroup[]>(() => {
  if (!hasQuoteVariants.value || !product.value) return [];
  const hasSizeButtons = (product.value.sizes?.length ?? 0) > 0;
  return product.value.combinationOptions.filter(
    (group) => !isSizeGroup(group) || !hasSizeButtons,
  );
});

// Klucz grupy w adresie: `?kolor=czarny`, tak jak `?rozmiar=L`.
const variantQueryKey = (groupName: string) => groupName.trim().toLowerCase();

const variantPicksFromUrl = computed<VariantPicks>(() => {
  const picks: VariantPicks = {};
  for (const group of quoteVariantGroups.value) {
    const raw = route.query?.[variantQueryKey(group.name)];
    const wanted = typeof raw === "string" ? raw.trim().toLowerCase() : "";
    const item = wanted
      ? group.items.find((i) => i.value.trim().toLowerCase() === wanted)
      : undefined;
    if (item) picks[group.name] = item.id;
  }
  return picks;
});

const userVariantPicks = ref<VariantPicks>({});

// Wybór (rozmiar z selektora, potem kliknięcie/adres, potem kombinacja
// domyślna) i przyciski tylko z kolorami, które w wybranym rozmiarze istnieją —
// niedostępne nie rozpychały widoku. Kolor, którego nie ma w nowo wybranym
// rozmiarze, ustępuje domyślnemu, a po powrocie do rozmiaru wraca.
const variantSelection = computed(() =>
  hasQuoteVariants.value && product.value
    ? resolveVariantSelection(
        product.value.combinationOptions,
        product.value.combinations,
        {
          sizeLabel: selectedSize.value,
          shown: quoteVariantGroups.value,
          preferred: { ...variantPicksFromUrl.value, ...userVariantPicks.value },
        },
      )
    : { picks: {}, groups: [] },
);
const variantPicks = computed<VariantPicks>(() => variantSelection.value.picks);

function pickedVariantValue(group: ProductCombinationOptionGroup): string {
  return (
    group.items.find((item) => item.id === variantPicks.value[group.name])
      ?.value ?? ""
  );
}

// Kombinacja odpowiadająca wybranemu wariantowi — źródło stanu, koszyka
// i zapytania o wycenę. Bez niej karta pokazywała sumę stanów wszystkich
// kombinacji, a zapytanie szło dla kombinacji domyślnej (nazwa/SKU produktu).
const selectedCombination = computed<SelectedCombination | undefined>(() => {
  if (hasQuoteVariants.value && product.value) {
    return resolveQuoteVariant(
      product.value.combinationOptions,
      product.value.combinations,
      variantPicks.value,
    );
  }
  return hasNativeCombinations.value && product.value
    ? resolveSelectedCombination(
        product.value.combinationOptions,
        product.value.combinations,
        selectedSize.value,
      )
    : undefined;
});

// Stan wybranej kombinacji, a gdy kombinacji nie ma — stan produktu (w serii
// allegrohash `product.quantity` jest już stanem wybranego rodzeństwa).
const availableQuantity = computed(
  () =>
    selectedCombination.value?.combination.quantity ??
    product.value?.quantity ??
    0,
);

watch(slug, () => {
  localSelectedSize.value = "";
  userVariantPicks.value = {};
  selectedVariantSlug.value = null;
});

const router = useRouter();

function onSizeSelect(rozmiar: string) {
  localSelectedSize.value = rozmiar;
  // Adres ma pokazywać wybrany wariant (udostępniony link otwiera ten rozmiar).
  // `replace`, nie `push` — zmiana rozmiaru nie jest nowym krokiem w historii.
  // Tylko marki z `pdpSizeInUrl`: replace odpala page_view (dataLayer/Meta).
  if (brand.pdpSizeInUrl && hasNativeCombinations.value) {
    void router.replace({ query: { ...route.query, rozmiar } });
  }
  const variants = baseProduct.value?.sizeVariants ?? [];
  // Ten sam rozmiar może mieć kilka produktów-rodzeństwa (duplikaty); wybieramy
  // wariant DOSTĘPNY, nie pierwszy po etykiecie — inaczej trafimy na wyprzedzony
  // duplikat i przycisk „Dodaj do koszyka" zniknie. Zob. app/utils/sizeVariants.
  const variant = resolveSizeVariant(variants, rozmiar);
  if (!variant || variant.isCurrent) {
    selectedVariantSlug.value = null;
  } else {
    selectedVariantSlug.value = `${variant.id}-${variant.slug}`;
  }
}

function onVariantSelect(groupName: string, item: { id: number; value: string }) {
  userVariantPicks.value = { ...userVariantPicks.value, [groupName]: item.id };
  // Jak przy rozmiarze: udostępniony link otwiera ten kolor (`?kolor=`).
  if (brand.pdpSizeInUrl) {
    void router.replace({
      query: { ...route.query, [variantQueryKey(groupName)]: item.value },
    });
  }
}

// Produkt bez rozmiarów (np. słuchawki w jednym kolorze) nie ma czego
// pokazać po „Rozmiar:" — w katalogu etykietę chowamy, zostaje dostępność.
const showSizeLabel = computed(
  () =>
    sellingEnabled ||
    (product.value?.sizeVariants?.length ?? 0) > 0 ||
    (product.value?.sizes?.length ?? 0) > 0,
);

const isSoldOut = computed(
  () =>
    !!product.value &&
    availableQuantity.value <= 0 &&
    !product.value.allowOutOfStock,
);

// Czy nad zdjęciem (mobile) renderuje się ProductPromoOverlay — musi pokrywać
// się z v-if overlayu, bo na mobile badge "Nowość" jest wciągany na dół zdjęcia
// (margin-top: -40px) i bez tego zasłaniałby tekst kodu rabatowego z overlayu.
// Overlay pokazuje już tylko kod rabatowy — carrier hours są w pływającym pasku CTA.
const promoOverlayVisible = computed(() => !!noticeCoupon.value);

// Widoczność bloku .pdp__notices (kod rabatowy + countdown). Wspólne źródło dla
// v-if i watchera animacji intro — highlightedCoupon pochodzi z koszyka i bywa
// klient-late, więc notices animujemy osobno (patrz _revealPdpNotices).
const noticesVisible = computed(
  () => !!(noticeCoupon.value || product.value?.promoEndDate),
);

const stockIndicator = computed(() => {
  if (!product.value) return null;
  const quantity = availableQuantity.value;

  if (product.value.allowOutOfStock && quantity <= 0) {
    return { label: "Na zamówienie", tone: "yellow" };
  }

  if (quantity > 2) {
    return { label: `Dostępny (${quantity} szt.)`, tone: "green" };
  }

  if (quantity === 2) {
    return { label: "Ostatnie sztuki (2 szt.)", tone: "yellow" };
  }

  if (quantity === 1) {
    return { label: "Ostatnia sztuka (1 szt.)", tone: "yellow" };
  }

  return { label: "Niedostępny", tone: "red" };
});

const isFavorite = computed(
  () => wishlist.value?.productIds?.includes(product.value?.id ?? "") || false,
);
const brand = useBrand();
// Kółko koloru przy nazwie (BrandConfig.colorSwatches) — tylko marki z flagą,
// pozostałe zostają przy samych napisach. Komponent leniwy: bez flagi jego
// kod (z mapą kolorów) nie trafia do przeglądarki.
const showColorSwatches = brand.colorSwatches === true;

// Galeria wybranego koloru (BrandConfig.pdpGalleryFollowsColor). Kolor z tego
// samego wyboru co przyciski (`variantPicks`: klik, `?kolor=`, kombinacja
// domyślna) — liczony już w SSR, więc HTML z serwera ma od razu zdjęcia
// koloru z adresu. Pozostałe marki: undefined → ProductGallery jak dotąd.
// Ta sama lista → ta sama tablica (`previous`): wybór przelicza się przy
// KAŻDEJ zmianie route.query (np. dopisany `utm_source`), a nowa tablica
// cofałaby galerię na pierwsze zdjęcie (watch w ProductGallery).
const colorGalleryImages = computed<string[] | undefined>((previous) => {
  const items = product.value?.galleryItems;
  if (brand.pdpGalleryFollowsColor !== true || !items?.length) return undefined;
  const next = variantGallery(
    items,
    pickedColorIds(product.value?.combinationOptions ?? [], variantPicks.value),
  );
  return previous &&
    previous.length === next.length &&
    previous.every((url, index) => url === next[index])
    ? previous
    : next;
});

// Pozycja listy zapytania dostaje zdjęcie wybranego koloru (pierwsze z jego
// galerii), nie okładkę koloru głównego. Bez flagi colorGalleryImages jest
// undefined → okładka jak dotąd. og:image zostaje przy thumbnail.
const quoteListImage = computed(
  () => colorGalleryImages.value?.[0] ?? product.value?.thumbnail,
);

// Okruchy między „Strona główna" a produktem. Domyślnie sama kategoria
// domyślna produktu; marka z `pdpBreadcrumbNavPath` dostaje ścieżkę z menu,
// bo podkategoria bez działu („Inne tekstylia i odzież" bez „Tekstylia")
// nie zgadzała się z nawigacją.
const categoryTrail = computed<Array<{ label: string; href: string | null }>>(
  () => {
    const p = product.value;
    if (!p) return [];
    if (brand.pdpBreadcrumbNavPath && p.categoryId) {
      const path = findNavPath(nav.value?.items ?? [], p.categoryId);
      if (path?.length) {
        return path.map((item) => ({ label: item.label, href: item.href }));
      }
    }
    return [{ label: p.categoryName, href: categoryHref.value }];
  },
);

const brandLink = computed(() =>
  product.value?.brandId && product.value?.brandSlug
    ? `/marka/${product.value.brandId}-${product.value.brandSlug}`
    : "",
);
const discountPercent = computed(() => {
  if (!product.value?.compareAtPrice || !product.value.price) return null;
  const pct = Math.round(
    (1 - product.value.price / product.value.compareAtPrice) * 100,
  );
  return pct > 0 ? `-${pct}%` : null;
});

function resolveAddToCartTarget() {
  if (!product.value) return null;

  const productId = Number(product.value.id);
  const selected = (
    selectedSize.value ||
    product.value.defaultSize ||
    ""
  ).trim();

  if ((product.value.sizeVariants?.length ?? 0) > 0) {
    // Preferuj wariant dostępny (nie pierwszy po etykiecie) — przy duplikatach
    // rozmiaru chroni przed dodaniem do koszyka wyprzedzonego rodzeństwa.
    const matchedVariant = resolveSizeVariant(
      product.value.sizeVariants,
      selected,
    );

    if (!matchedVariant) return null;

    const resolvedProductId = Number(matchedVariant.id);
    if (!Number.isFinite(resolvedProductId) || resolvedProductId <= 0)
      return null;

    return { productId: resolvedProductId, productAttributeId: 0 };
  }

  if (
    (product.value.combinationOptions?.length ?? 0) > 0 &&
    (product.value.combinations?.length ?? 0) > 0
  ) {
    // Ta sama kombinacja co stan i zapytanie o wycenę. Dawne porównanie kodu
    // z samym id rozmiaru nie trafiało w produkty wielowymiarowe („3_217").
    const resolvedCombination = selectedCombination.value?.combination;

    if (
      !resolvedCombination ||
      !Number.isFinite(resolvedCombination.idProductAttribute) ||
      resolvedCombination.idProductAttribute <= 0
    ) {
      return null;
    }

    return {
      productId: Number.isFinite(productId) ? productId : 0,
      productAttributeId: resolvedCombination.idProductAttribute,
    };
  }

  return {
    productId: Number.isFinite(productId) ? productId : 0,
    productAttributeId: 0,
  };
}

/**
 * Czego dotyczy zapytanie o wycenę / pozycja listy zapytania — AKTUALNIE
 * wybrany wariant, nie ten, z którym weszliśmy na kartę.
 *
 * Wcześniej CTA brało `product.id` + SKU produktu: przy natywnych kombinacjach
 * to kombinacja domyślna („BOSTON Blekitny XXL", S16000-SK-XXL), a w serii
 * allegrohash — produkt z adresu, nie wybrane rodzeństwo. Cel liczymy tak samo
 * jak dla koszyka (resolveAddToCartTarget), więc oba tory mówią o tym samym.
 */
const quoteTarget = computed(() => {
  const current = product.value;
  if (!current) return null;
  const target = resolveAddToCartTarget();
  const idProduct = target?.productId || current.id;
  const combination = selectedCombination.value;
  // SKU: własne SKU wybranej kombinacji (combinations[].reference), a gdy API go
  // nie podaje — SKU produktu tylko dla wariantu wejściowego. SKU produktu
  // należy do kombinacji domyślnej (albo do produktu z adresu), więc przy innym
  // wyborze byłby nieprawdziwy; backend i tak dopisze właściwe dane z bazy.
  const isEntryVariant = combination
    ? combination.isDefault
    : String(idProduct) === current.id;
  const reference =
    combination?.combination.reference || (isEntryVariant ? current.sku : "");
  const attributes = combination
    ? combinationLabel(combination)
    : (current.sizeVariants?.length ?? 0) > 0 && selectedSize.value
      ? `Rozmiar: ${selectedSize.value}`
      : "";
  return {
    idProduct,
    idProductAttribute: target?.productAttributeId ?? 0,
    name: current.name,
    reference,
    attributes,
  };
});

// Link z listy zapytania wraca do karty z TYM wariantem (`?rozmiar=`,
// w katalogu też `?kolor=` i reszta grup).
const quoteListLink = computed(() => {
  const params: Record<string, string> = {};
  if (selectedCombination.value && selectedSize.value) {
    params.rozmiar = selectedSize.value;
  }
  for (const group of quoteVariantGroups.value) {
    const value = pickedVariantValue(group);
    if (value) params[variantQueryKey(group.name)] = value;
  }
  const query = Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");
  return query ? `${routeProductPath.value}?${query}` : routeProductPath.value;
});

function requestQuote() {
  const target = quoteTarget.value;
  if (!target) return;
  openQuote({
    idProduct: target.idProduct,
    name: target.name,
    reference: target.reference,
    ...(target.idProductAttribute > 0
      ? { idProductAttribute: target.idProductAttribute }
      : {}),
    ...(target.attributes ? { attributes: target.attributes } : {}),
  });
}

const atStockLimit = computed(() => {
  const target = resolveAddToCartTarget();
  if (!target) return false;
  return addToCartFlow.isAtStockLimit?.(target) ?? false;
});

const sanitizedDescription = computed(() => {
  const value = product.value?.description ?? "";
  const stripped = value
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/\n{3,}/g, "\n\n");

  return (
    stripped
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .trim()
      // Treść z PrestaShop często zaczyna się od własnego nagłówka "OPIS" /
      // "OPIS PRODUKTU", który dubluje tytuł sekcji akordeonu — usuń go.
      .replace(/^\s*opis(\s+produktu)?\s*\n+/i, "")
      .trim()
  );
});

function addToCart() {
  const target = resolveAddToCartTarget();
  if (!target) return;
  const currentProduct = product.value;
  void addToCartFlow
    .add({
      productId: target.productId,
      productAttributeId: target.productAttributeId,
      quantity: 1,
    })
    .then(() => {
      if (currentProduct && !addToCartFlow.stockError.value) {
        analytics.trackAddToCart(currentProduct, 1, {
          variant: selectedSize.value || undefined,
        });
      }
    })
    .catch(() => {});
}

function toggleFavorite() {
  if (!product.value) return;
  const wasFavorite = isFavorite.value;
  const currentProduct = product.value;
  toggleWishlist.mutate(
    {
      productId: currentProduct.id,
      isFavorite: wasFavorite,
    },
    {
      onSuccess: () => {
        if (wasFavorite) analytics.trackRemoveFromWishlist(currentProduct);
        else analytics.trackAddToWishlist(currentProduct);
      },
    },
  );
}

function toggleAccordion(key: string) {
  openAccordion.value = openAccordion.value === key ? null : key;
}

// ── GSAP product-page animations ──
type _PdpGsapContext = { revert(): void };
type _PdpGsapApi = {
  registerPlugin(p: unknown): void;
  context(fn: () => void, scope?: Element | null): _PdpGsapContext;
  fromTo(
    targets: HTMLElement | HTMLElement[],
    from: Record<string, unknown>,
    to: Record<string, unknown>,
  ): void;
};
type _PdpStApi = { getAll(): Array<{ kill(): void }> };

let _pdpGsap: _PdpGsapApi | null = null;
let _pdpSt: _PdpStApi | null = null;
let _pdpIntroCtx: _PdpGsapContext | null = null;
let _pdpScrollCtx: _PdpGsapContext | null = null;
let _pdpNoticesCtx: _PdpGsapContext | null = null;
let _pdpUnwatchAnim: (() => void) | null = null;
let _pdpUnwatchNotices: (() => void) | null = null;
let _pdpAnimatedProductId: string | null = null;

const _PDP_REVEAL = { duration: 0.72, ease: "power2.out" } as const;
const _PDP_ST_OPTS = {
  start: "top 86%",
  toggleActions: "play none none none",
} as const;
const _PDP_PANEL_SELECTOR = [
  ".pdp__badges",
  ".pdp__brand",
  ".pdp__name",
  ".pdp__rating",
  ".pdp__price-block",
  ".pdp__divider",
  ".pdp__field",
  ".pdp__notice--cta",
  ".pdp__cta",
  ".pdp__stock-error",
  ".pdp__standing-codes",
  ".pdp__accordions",
].join(", ");

function _pdpEls(selector: string): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(selector));
}

function _pdpEl(selector: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(selector);
}

function _pdpHasReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function _animatePdpIntro(current: Product): void {
  if (!_pdpGsap || _pdpAnimatedProductId === current.id) return;
  const breadcrumb = _pdpEl(".pdp__breadcrumb");
  const media = _pdpEl(".pdp__media");
  const panelItems = _pdpEls(_PDP_PANEL_SELECTOR);
  const galleryImages = _pdpEls(
    ".pdp__media .gallery__desktop-img, .pdp__media .gallery__thumb-img, .pdp__media .gallery__main-img",
  );

  if (!breadcrumb && !media && !panelItems.length) return;

  _pdpIntroCtx?.revert();
  _pdpIntroCtx = _pdpGsap.context(() => {
    const g = _pdpGsap!;

    if (breadcrumb) {
      g.fromTo(
        breadcrumb,
        { opacity: 0, y: 14 },
        { ..._PDP_REVEAL, opacity: 1, y: 0 },
      );
    }

    if (media) {
      g.fromTo(
        media,
        { opacity: 0, y: 28 },
        { ..._PDP_REVEAL, opacity: 1, y: 0, delay: 0.05 },
      );
    }

    if (galleryImages.length) {
      g.fromTo(
        galleryImages,
        { scale: 1.06 },
        {
          scale: 1,
          duration: 0.9,
          ease: "power2.out",
          stagger: 0.04,
          clearProps: "scale",
          delay: 0.1,
        },
      );
    }

    if (panelItems.length) {
      g.fromTo(
        panelItems,
        { opacity: 0, y: 24 },
        {
          ..._PDP_REVEAL,
          opacity: 1,
          y: 0,
          stagger: 0.055,
          delay: 0.12,
        },
      );
    }
  }, document.body);

  _pdpAnimatedProductId = current.id;
}

// .pdp__notices animujemy OSOBNO, nie przez _PDP_PANEL_SELECTOR. Powód: blok
// zależy od highlightedCoupon (koszyk), który bywa klient-late — potrafi
// zamontować się długo po tym, jak intro już przejechało. CSS trzyma go na
// opacity:0 (anty-FOUC), więc bez dedykowanego revealu zostałby uwięziony
// (dokładnie regresja, którą poprzedni autor obszedł wykluczając notices).
// Watcher na noticesVisible wywołuje tę funkcję i na wejściu, i przy późnym
// kuponie; przy ukryciu kontekst jest revertowany, więc remount re-animuje.
function _revealPdpNotices(): void {
  if (!_pdpGsap) return;
  const notices = _pdpEl(".pdp__notices");
  if (!notices) return;
  _pdpNoticesCtx?.revert();
  _pdpNoticesCtx = _pdpGsap.context(() => {
    _pdpGsap!.fromTo(
      notices,
      { opacity: 0, y: 24 },
      { ..._PDP_REVEAL, opacity: 1, y: 0, delay: 0.4 },
    );
  }, document.body);
}

function _animatePdpScrollSections(): void {
  if (!_pdpGsap) return;
  const recommendationSections = _pdpEls(
    ".product-recommendations, .product-editorial-section",
  );
  const newsletter = _pdpEl(".newsletter");
  const newsletterItems = _pdpEls(".newsletter__head, .newsletter__form");

  _pdpScrollCtx?.revert();
  _pdpScrollCtx = _pdpGsap.context(() => {
    const g = _pdpGsap!;

    recommendationSections.forEach((section, index) => {
      g.fromTo(
        section,
        { opacity: 0, y: 34 },
        {
          ..._PDP_REVEAL,
          opacity: 1,
          y: 0,
          delay: index * 0.04,
          scrollTrigger: { trigger: section, ..._PDP_ST_OPTS },
        },
      );
    });

    const productImages = recommendationSections.flatMap((section) =>
      Array.from(
        section.querySelectorAll<HTMLElement>(
          ".product-card__img:not(.product-card__img--alt)",
        ),
      ),
    );
    if (productImages.length) {
      g.fromTo(
        productImages,
        { scale: 1.05 },
        {
          scale: 1,
          duration: 0.8,
          ease: "power2.out",
          stagger: 0.045,
          clearProps: "scale",
          scrollTrigger: {
            trigger: recommendationSections[0] ?? productImages[0],
            ..._PDP_ST_OPTS,
          },
        },
      );
    }

    if (newsletter && newsletterItems.length) {
      g.fromTo(
        newsletterItems,
        { opacity: 0, y: 30 },
        {
          ..._PDP_REVEAL,
          opacity: 1,
          y: 0,
          stagger: 0.1,
          scrollTrigger: { trigger: newsletter, ..._PDP_ST_OPTS },
        },
      );
    }
  }, document.body);
}

onMounted(async () => {
  if (typeof window === "undefined" || _pdpHasReducedMotion()) {
    introAnimationsEnabled.value = false;
    return;
  }

  try {
    const [{ gsap: g }, { ScrollTrigger: ST }] = await Promise.all([
      import("gsap"),
      import("gsap/ScrollTrigger"),
    ]);
    _pdpGsap = g as _PdpGsapApi;
    _pdpSt = ST as _PdpStApi;
    _pdpGsap.registerPlugin(_pdpSt);
  } catch {
    introAnimationsEnabled.value = false;
    return;
  }

  _pdpUnwatchAnim = watch(
    [product, isRecommendationsPending, othersAlsoViewed, bestInCategory],
    async ([current]) => {
      if (!current) return;
      await nextTick();
      await nextTick();
      _animatePdpIntro(current);
      _animatePdpScrollSections();
    },
    { immediate: true },
  );

  // Osobny reveal dla .pdp__notices — łapie i wejście, i klient-late kupon.
  // Bramkujemy NA noticesVisible ORAZ product: cały blok .pdp__notices wisi pod
  // root v-if="product", więc dopóki produkt się nie załaduje, elementu nie ma
  // w DOM. Przy SPA nawigacji highlightedCoupon (vue-query, koszyk) jest
  // cache'owany i noticesVisible bywa true ZANIM klient-side fetch produktu
  // zdąży wyrenderować notices — sam noticesVisible odpaliłby reveal w pusty
  // DOM i, skoro getter zostaje true, nigdy by nie ponowił, więżąc blok na
  // opacity:0 (regresja przy wejściu z kategorii kliknięciem). Keyowanie na
  // product (jak watcher intro powyżej) gwarantuje, że trigger flipuje na true
  // dopiero, gdy poddrzewo .pdp faktycznie istnieje. Dwa nextTick czekają na
  // przeliczenie v-if samego bloku.
  _pdpUnwatchNotices = watch(
    () => noticesVisible.value && !!product.value,
    async (ready) => {
      if (!ready) {
        _pdpNoticesCtx?.revert();
        _pdpNoticesCtx = null;
        return;
      }
      await nextTick();
      await nextTick();
      _revealPdpNotices();
    },
    { immediate: true },
  );
});

onUnmounted(() => {
  _pdpUnwatchAnim?.();
  _pdpUnwatchNotices?.();
  _pdpIntroCtx?.revert();
  _pdpScrollCtx?.revert();
  _pdpNoticesCtx?.revert();
  _pdpSt?.getAll().forEach((t) => t.kill());
  _pdpGsap = null;
  _pdpSt = null;
  _pdpIntroCtx = null;
  _pdpScrollCtx = null;
  _pdpNoticesCtx = null;
  _pdpUnwatchAnim = null;
  _pdpUnwatchNotices = null;
  _pdpAnimatedProductId = null;
});
</script>

<template>
  <div
    v-if="product"
    class="pdp"
    :class="{ 'pdp--intro-animations': introAnimationsEnabled }"
  >
    <!-- ── Breadcrumb ── -->
    <div
      class="pdp__breadcrumb container"
      :class="{ 'pdp__breadcrumb--trail': categoryTrail.length > 1 }"
    >
      <NuxtLink to="/" class="pdp__breadcrumb-link">Strona główna</NuxtLink>
      <!-- Klucz po pozycji i adresie, nie po nazwie: dział i podkategoria
           potrafią się nazywać tak samo. -->
      <template
        v-for="(crumb, index) in categoryTrail"
        :key="`${index}:${crumb.href ?? crumb.label}`"
      >
        <span class="pdp__breadcrumb-sep">/</span>
        <NuxtLink
          v-if="crumb.href"
          :to="crumb.href"
          class="pdp__breadcrumb-link"
          >{{ crumb.label }}</NuxtLink
        >
        <span v-else class="pdp__breadcrumb-link">{{ crumb.label }}</span>
      </template>
      <span class="pdp__breadcrumb-sep">/</span>
      <span class="pdp__breadcrumb-current">{{ product.name }}</span>
    </div>

    <!-- ── Main product layout ── -->
    <div
      class="pdp__layout container"
      :class="{ 'pdp__layout--compact-media': brand.pdpGalleryLayout === 'main-thumbs' }"
    >
      <!-- Gallery -->
      <div class="pdp__media">
        <ProductGallery :product="product" :images="colorGalleryImages" />
        <div
          v-if="discountPercent"
          class="badge-sale pdp__promo-badge pdp__promo-badge--media"
        >
          {{ discountPercent }}
        </div>
        <ProductPromoOverlay
          v-if="noticeCoupon"
          class="pdp__promo-overlay"
          :coupon="noticeCoupon"
        />
      </div>

      <!-- Info panel -->
      <article class="pdp__panel">
        <!-- Badges -->
        <div
          v-if="product.badges?.length"
          class="pdp__badges"
          :class="{ 'pdp__badges--overlay-clear': promoOverlayVisible }"
        >
          <span
            v-for="badge in product.badges"
            :key="badge"
            class="badge-new"
            >{{ badge }}</span
          >
        </div>

        <!-- Brand + Name -->
        <NuxtLink
          v-if="product.brand && brandLink"
          :to="brandLink"
          class="pdp__brand"
          >{{ product.brand }}</NuxtLink
        >
        <p v-else class="pdp__brand">{{ product.brand }}</p>
        <h1 class="pdp__name">{{ product.name }}</h1>

        <!-- Rating (ocena sklepu z Zaufane.pl/eKomi) -->
        <RatingScrollTrigger
          v-if="shopReviews?.available && shopReviews.count > 0"
          :rating="shopReviews.average"
          :count="shopReviews.count"
        />

        <!-- Price -->
        <div class="pdp__price-block">
          <div class="pdp__price-row">
            <span
              class="pdp__price"
              :class="{ 'pdp__price--sale': product.compareAtPrice }"
              >{{ formatPrice(product.price) }}</span
            >
            <span v-if="brand.priceTaxNote" class="pdp__price-tax">{{
              brand.priceTaxNote
            }}</span>
            <span v-if="product.compareAtPrice" class="pdp__compare">{{
              formatPrice(product.compareAtPrice)
            }}</span>
          </div>
          <p
            v-if="product.lowestRecentPrice && product.compareAtPrice"
            class="pdp__lowest"
          >
            Najniższa cena w okresie 30 dni:
            {{ formatPrice(product.lowestRecentPrice) }}
          </p>
        </div>

        <div class="pdp__divider" />

        <!-- Sizes -->
        <div class="pdp__field">
          <div class="pdp__field-row">
            <p v-if="showSizeLabel" class="pdp__field-label">
              Rozmiar: <span class="pdp__field-value">{{ selectedSize }}</span>
            </p>
            <p v-if="stockIndicator" class="pdp__availability">
              <span
                class="pdp__availability-dot"
                :class="`pdp__availability-dot--${stockIndicator.tone}`"
              />
              <span>{{ stockIndicator.label }}</span>
            </p>
          </div>
          <ProductSizeSelector
            :variants="product.sizeVariants"
            :fallback-sizes="product.sizes"
            :selected-size="selectedSize"
            @select="onSizeSelect"
          />
          <ProductSizeMeasurements
            :size-chart="product.sizeChart"
            :selected-size="selectedSize"
          />
          <ProductSizeChartModal
            :size-chart="product.sizeChart"
            :selected-size="selectedSize"
          />
        </div>

        <!-- Sklep katalogowy: pozostałe grupy atrybutów (kolor…), tylko wartości
             istniejące przy wyborze wyżej. Wycena idzie dokładnie dla
             kombinacji wybranej tutaj i rozmiarem wyżej. -->
        <div
          v-for="group in variantSelection.groups"
          :key="group.name"
          class="pdp__field pdp__variant-group"
        >
          <p class="pdp__field-label">
            {{ group.name }}:
            <span class="pdp__field-value">{{ pickedVariantValue(group) }}</span>
          </p>
          <div class="pdp__variant-options" role="group" :aria-label="group.name">
            <button
              v-for="item in group.items"
              :key="item.id"
              type="button"
              class="pdp__variant-btn"
              :class="{
                'pdp__variant-btn--active': variantPicks[group.name] === item.id,
              }"
              :aria-pressed="variantPicks[group.name] === item.id"
              @click="onVariantSelect(group.name, item)"
            >
              <LazyProductColorSwatch
                v-if="showColorSwatches && group.isColor"
                :name="item.value"
                :hex="item.hex"
              />
              {{ item.value }}
            </button>
          </div>
        </div>

        <!-- Stock alert (sold out) -->
        <ProductStockAlert
          v-if="isSoldOut"
          :product-id="product.id"
          :is-favorite="isFavorite"
          @toggle-wishlist="toggleFavorite"
        />

        <!-- CTA -->
        <div v-else class="pdp__cta-wrap">
          <div class="pdp__cta" :class="{ 'pdp__cta--quote': !sellingEnabled }">
            <button
              v-if="sellingEnabled"
              type="button"
              class="pdp__add-to-cart"
              :disabled="addToCartFlow.isPending.value || atStockLimit"
              @click="addToCart"
            >
              Dodaj do koszyka
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
            </button>
            <!-- Tryb katalogowy: główną akcją karty produktu jest zapytanie
                 o wycenę, dokładnie w miejscu przycisku zakupu.
                 Przycisk, nie NuxtLink: `@click.prevent` nie zatrzymuje
                 nawigacji NuxtLinka (ten routuje własnym handlerem, nie
                 domyślną akcją zdarzenia), więc modal otwierał się i ginął,
                 bo strona natychmiast szła na /contact. -->
            <button
              v-else
              type="button"
              class="pdp__add-to-cart"
              @click="requestQuote"
            >
              {{ ctaLabel }}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-10 6L2 7" />
              </svg>
            </button>
            <!-- Zbieranie produktów przed zapytaniem: klient B2B prawie nigdy
                 nie pyta o jedną pozycję. Wysyłką zajmuje się /lista-zapytania. -->
            <QuoteListButton
              v-if="!sellingEnabled && quoteTarget"
              :id-product="quoteTarget.idProduct"
              :id-product-attribute="quoteTarget.idProductAttribute"
              :attributes="quoteTarget.attributes"
              :name="quoteTarget.name"
              :reference="quoteTarget.reference"
              :image="quoteListImage"
              :link="quoteListLink"
            />
            <!-- Ulubione wymagają konta — w sklepie katalogowym ich nie ma
                 (/favorites zablokowane), serduszko tylko udawało zapis. -->
            <button
              v-if="sellingEnabled"
              type="button"
              class="pdp__wishlist-btn"
              :class="{ 'is-favorite': isFavorite }"
              :aria-label="
                isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'
              "
              @click="toggleFavorite"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                :fill="isFavorite ? 'currentColor' : 'none'"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path
                  d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                />
              </svg>
            </button>
          </div>
          <p v-if="addToCartFlow.stockError.value" class="pdp__stock-error">
            Brak wystarczającej ilości w magazynie.
          </p>
          <p v-if="atStockLimit" class="pdp__stock-limit">
            Więcej nie mamy — to ostatnie sztuki dla Ciebie, już w koszyku
          </p>
          <ClientOnly>
            <!-- InPost Pay to zakup jednym kliknięciem — w katalogu bez
                 sprzedaży nie ma go czym obsłużyć (backend odrzuca 403). -->
            <InpostPayButton
              v-if="sellingEnabled"
              context="product"
              :product-id="inpostpayProductId"
            />
          </ClientOnly>
          <p
            v-if="!isSoldOut && carrierHoursText"
            class="pdp__notice pdp__notice--cta"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            <ColorMarkedText :text="carrierHoursText" />
          </p>
        </div>

        <!-- Stałe kody rabatowe: im więcej kupujesz, tym więcej zyskujesz (klik = kopiuj) -->
        <PromoStandingCodes class="pdp__standing-codes" copy-on-click />

        <!-- Promo notices (bez kodów EXTRA — te mają własny blok wyżej) -->
        <div v-if="noticesVisible" class="pdp__notices">
          <ProductPromoCode v-if="noticeCoupon" :coupon="noticeCoupon" />
          <ProductPromoCountdown
            v-if="noticeCoupon?.dateTo || product.promoEndDate"
            :end-date="noticeCoupon?.dateTo || product.promoEndDate"
          />
        </div>

        <!-- Accordions -->
        <div class="pdp__accordions">
          <!-- Description -->
          <div class="pdp__accordion">
            <button
              type="button"
              class="pdp__accordion-header"
              :aria-expanded="openAccordion === 'desc'"
              @click="toggleAccordion('desc')"
            >
              <span class="pdp__accordion-title">
                <svg
                  class="pdp__accordion-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="14" y2="17" />
                </svg>
                Opis produktu
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path
                  :d="
                    openAccordion === 'desc'
                      ? 'M3 10L8 5L13 10'
                      : 'M3 6L8 11L13 6'
                  "
                />
              </svg>
            </button>
            <div v-show="openAccordion === 'desc'" class="pdp__accordion-body">
              <p class="pdp__description">{{ sanitizedDescription }}</p>
            </div>
          </div>

          <!-- Details -->
          <div class="pdp__accordion">
            <button
              type="button"
              class="pdp__accordion-header"
              :aria-expanded="openAccordion === 'details'"
              @click="toggleAccordion('details')"
            >
              <span class="pdp__accordion-title">
                <svg
                  class="pdp__accordion-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <line x1="12" y1="8" x2="12" y2="8" />
                  <polyline points="11 12 12 12 12 16 13 16" />
                </svg>
                Szczegóły produktu i skład
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path
                  :d="
                    openAccordion === 'details'
                      ? 'M3 10L8 5L13 10'
                      : 'M3 6L8 11L13 6'
                  "
                />
              </svg>
            </button>
            <div
              v-show="openAccordion === 'details'"
              class="pdp__accordion-body"
            >
              <dl v-if="product.productInfo.length" class="pdp__info-grid">
                <template v-for="item in product.productInfo" :key="item.name">
                  <div class="pdp__info-item">
                    <dt class="pdp__info-label">{{ item.name }}</dt>
                    <dd class="pdp__info-value">{{ item.value }}</dd>
                  </div>
                </template>
              </dl>
              <p v-else class="pdp__material">Brak szczegółów.</p>
            </div>
          </div>

          <!-- Tryb katalogowy: zamiast sklepowych zakładek (wysyłka 24h od
               płatności, zwrot w 14 dni, BLIK) — przebieg zapytania. Treść
               wspólna z sekcją „Jak to działa" strony głównej, żeby karta
               niczego ponad nią nie obiecywała. -->
          <div v-if="!sellingEnabled" class="pdp__accordion">
            <button
              type="button"
              class="pdp__accordion-header"
              :aria-expanded="openAccordion === 'process'"
              @click="toggleAccordion('process')"
            >
              <span class="pdp__accordion-title">
                <svg
                  class="pdp__accordion-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                  <rect x="9" y="3" width="6" height="4" rx="1" />
                  <path d="m9 14 2 2 4-4" />
                </svg>
                Jak to działa
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path
                  :d="
                    openAccordion === 'process'
                      ? 'M3 10L8 5L13 10'
                      : 'M3 6L8 11L13 6'
                  "
                />
              </svg>
            </button>
            <div
              v-show="openAccordion === 'process'"
              class="pdp__accordion-body"
            >
              <ol class="pdp__accordion-list pdp__process-list">
                <li v-for="step in CATALOG_HOW_IT_WORKS_STEPS" :key="step.title">
                  <strong>{{ step.title }}.</strong> {{ step.text }}
                </li>
              </ol>
              <p>
                <NuxtLink to="/techniki-znakowania" class="pdp__accordion-link"
                  >Techniki znakowania</NuxtLink
                >
                — jak dobrać technikę do produktu, nakładu i projektu.
              </p>
            </div>
          </div>

          <!-- Delivery -->
          <div v-if="sellingEnabled" class="pdp__accordion">
            <button
              type="button"
              class="pdp__accordion-header"
              :aria-expanded="openAccordion === 'delivery'"
              @click="toggleAccordion('delivery')"
            >
              <span class="pdp__accordion-title">
                <svg
                  class="pdp__accordion-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <rect x="1" y="6" width="13" height="10" rx="1" />
                  <path d="M14 9h4l3 3v4h-7z" />
                  <circle cx="6" cy="18" r="2" />
                  <circle cx="17" cy="18" r="2" />
                </svg>
                Dostawa
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path
                  :d="
                    openAccordion === 'delivery'
                      ? 'M3 10L8 5L13 10'
                      : 'M3 6L8 11L13 6'
                  "
                />
              </svg>
            </button>
            <div
              v-show="openAccordion === 'delivery'"
              class="pdp__accordion-body"
            >
              <p>
                Wysyłka w ciągu 24h od zaksięgowania płatności. Sposób dostawy:
              </p>
              <ul class="pdp__accordion-list">
                <li>Kurier InPost</li>
                <li>Paczkomaty InPost</li>
              </ul>
              <p>
                Czas realizacji 1–2 dni robocze. Darmowa dostawa od
                {{ brand.freeShippingThreshold }}&nbsp;zł.
              </p>
            </div>
          </div>

          <!-- Returns -->
          <div v-if="sellingEnabled" class="pdp__accordion">
            <button
              type="button"
              class="pdp__accordion-header"
              :aria-expanded="openAccordion === 'returns'"
              @click="toggleAccordion('returns')"
            >
              <span class="pdp__accordion-title">
                <svg
                  class="pdp__accordion-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="3 7 3 12 8 12" />
                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                </svg>
                Zasady zwrotów
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path
                  :d="
                    openAccordion === 'returns'
                      ? 'M3 10L8 5L13 10'
                      : 'M3 6L8 11L13 6'
                  "
                />
              </svg>
            </button>
            <div
              v-show="openAccordion === 'returns'"
              class="pdp__accordion-body"
            >
              <p>
                Jeśli zamówiony produkt nie spełni Twoich oczekiwań, możesz
                dokonać jego zwrotu w ciągu 14 dni od daty otrzymania przesyłki.
              </p>
              <p>Aby zwrot został zaakceptowany, produkt powinien być:</p>
              <ul class="pdp__accordion-list">
                <li>nieużywany i bez śladów użytkowania,</li>
                <li>kompletny,</li>
                <li>posiadać wszystkie oryginalne metki i oznaczenia,</li>
                <li>
                  odesłany w oryginalnym opakowaniu (jeśli było dołączone).
                </li>
              </ul>
              <p>
                Po otrzymaniu i zweryfikowaniu zwracanego produktu, zwrot
                środków zostanie zrealizowany zgodnie z obowiązującymi
                przepisami prawa.
              </p>
            </div>
          </div>

          <!-- Payment -->
          <div v-if="sellingEnabled" class="pdp__accordion">
            <button
              type="button"
              class="pdp__accordion-header"
              :aria-expanded="openAccordion === 'payment'"
              @click="toggleAccordion('payment')"
            >
              <span class="pdp__accordion-title">
                <svg
                  class="pdp__accordion-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                  <line x1="6" y1="15" x2="10" y2="15" />
                </svg>
                Sposoby płatności
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path
                  :d="
                    openAccordion === 'payment'
                      ? 'M3 10L8 5L13 10'
                      : 'M3 6L8 11L13 6'
                  "
                />
              </svg>
            </button>
            <div
              v-show="openAccordion === 'payment'"
              class="pdp__accordion-body"
            >
              <p>
                Akceptujemy: BLIK, płatności online oraz płatność przy odbiorze.
              </p>
            </div>
          </div>
        </div>
      </article>
    </div>

    <ShopReviewsSection v-if="shopReviews" :data="shopReviews" />

    <ProductRecommendationSection
      v-if="isRecommendationsPending || othersAlsoViewed.length > 0"
      :title="isFallback ? 'Może Cię zainteresować' : 'Inni oglądali również'"
      :products="othersAlsoViewed"
      :desktop-columns="brand.pdpRecommendationColumns ?? 3"
      :is-loading="isRecommendationsPending"
      :item-list-id="`pdp_recommendations_${product.id}`"
      :item-list-name="
        isFallback ? 'Może Cię zainteresować' : 'Inni oglądali również'
      "
    />

    <ProductEditorialSection
      v-if="isRecommendationsPending || bestInCategory.length > 0"
      :title="isFallback ? 'Nowości w sklepie' : 'Najlepsze w tej kategorii'"
      :products="bestInCategory"
      :is-loading="isRecommendationsPending"
      :item-list-id="`pdp_best_in_category_${product.id}`"
      :item-list-name="
        isFallback ? 'Nowości w sklepie' : 'Najlepsze w tej kategorii'
      "
    />

    <!-- ── Newsletter ── -->
    <NewsletterSection />

    <Teleport to="body">
      <ProductStickyBar
        v-if="!isSoldOut"
        :is-pending="addToCartFlow.isPending.value"
        :disabled="atStockLimit"
        :is-favorite="isFavorite"
        :price="product.price"
        :carrier-hours-text="carrierHoursText"
        @add-to-cart="addToCart"
        @toggle-favorite="toggleFavorite"
        @request-quote="requestQuote"
      />
    </Teleport>
  </div>
  <div
    v-else-if="isProductLoading"
    class="pdp-loading container"
    aria-busy="true"
    aria-label="Ładowanie produktu"
  >
    <div class="pdp-loading__breadcrumb" aria-hidden="true">
      <span class="pdp-loading__line pdp-loading__line--crumb" />
      <span class="pdp-loading__dot" />
      <span class="pdp-loading__line pdp-loading__line--crumb-wide" />
      <span class="pdp-loading__dot" />
      <span class="pdp-loading__line pdp-loading__line--crumb-current" />
    </div>
    <div
      class="pdp-loading__layout"
      :class="{ 'pdp-loading__layout--compact-media': brand.pdpGalleryLayout === 'main-thumbs' }"
      aria-hidden="true"
    >
      <div
        class="pdp-loading__gallery"
        :class="{ 'pdp-loading__gallery--main-thumbs': brand.pdpGalleryLayout === 'main-thumbs' }"
      >
        <div class="pdp-loading__image" />
        <div class="pdp-loading__thumbs">
          <span v-for="n in 4" :key="n" class="pdp-loading__thumb" />
        </div>
      </div>
      <div class="pdp-loading__panel">
        <span class="pdp-loading__badge" />
        <span class="pdp-loading__brand" />
        <span class="pdp-loading__name" />
        <div class="pdp-loading__rating">
          <span v-for="n in 5" :key="n" class="pdp-loading__star" />
          <span class="pdp-loading__line pdp-loading__line--reviews" />
        </div>
        <div class="pdp-loading__price-block">
          <span class="pdp-loading__price" />
          <span class="pdp-loading__compare" />
          <span class="pdp-loading__line pdp-loading__line--lowest-price" />
        </div>
        <span class="pdp-loading__divider" />
        <div class="pdp-loading__field">
          <span class="pdp-loading__line pdp-loading__line--field-label" />
          <span class="pdp-loading__line pdp-loading__line--availability" />
        </div>
        <div class="pdp-loading__sizes">
          <span v-for="n in 5" :key="n" class="pdp-loading__size" />
        </div>
        <span class="pdp-loading__line pdp-loading__line--size-chart" />
        <div class="pdp-loading__cta">
          <span class="pdp-loading__button" />
          <!-- Bez ulubionych w sklepie katalogowym — szkielet nie rezerwuje
               miejsca, którego po załadowaniu nie będzie. -->
          <span v-if="sellingEnabled" class="pdp-loading__wishlist" />
        </div>
        <div class="pdp-loading__notices">
          <span class="pdp-loading__notice" />
          <span class="pdp-loading__notice pdp-loading__notice--short" />
        </div>
        <div class="pdp-loading__accordions">
          <span v-for="n in 4" :key="n" class="pdp-loading__accordion">
            <span class="pdp-loading__line pdp-loading__line--accordion" />
            <span class="pdp-loading__chevron" />
          </span>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="pdp-loading container" aria-busy="false">
    <p class="pdp-loading__title">Nie udało się załadować produktu</p>
  </div>
</template>

<style scoped>
.pdp {
  display: flex;
  flex-direction: column;
}

@media not (prefers-reduced-motion: reduce) {
  .pdp--intro-animations .pdp__breadcrumb,
  .pdp--intro-animations .pdp__media,
  .pdp--intro-animations .pdp__badges,
  .pdp--intro-animations .pdp__brand,
  .pdp--intro-animations .pdp__name,
  .pdp--intro-animations .pdp__rating,
  .pdp--intro-animations .pdp__price-block,
  .pdp--intro-animations .pdp__divider,
  .pdp--intro-animations .pdp__field,
  .pdp--intro-animations .pdp__notice--cta,
  .pdp--intro-animations .pdp__cta,
  .pdp--intro-animations .pdp__stock-error,
  .pdp--intro-animations .pdp__standing-codes,
  .pdp--intro-animations .pdp__notices,
  .pdp--intro-animations .pdp__accordions,
  .pdp--intro-animations .product-recommendations,
  .pdp--intro-animations .product-editorial-section {
    opacity: 0;
    transform: translateY(30px);
  }

  .pdp--intro-animations :deep(.newsletter__head),
  .pdp--intro-animations :deep(.newsletter__form) {
    opacity: 0;
    transform: translateY(30px);
  }
}

.pdp-loading {
  padding-top: 20px;
  padding-bottom: 60px;
}

.pdp-loading__breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 20px;
}

.pdp-loading__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: 0;
  align-items: start;
}

.pdp-loading__gallery {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 10px;
}

.pdp-loading__thumbs,
.pdp-loading__sizes {
  display: flex;
  gap: 8px;
}

.pdp-loading__panel {
  padding: 0 0 40px 40px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 16px;
}

.pdp-loading__line,
.pdp-loading__dot,
.pdp-loading__image,
.pdp-loading__thumb,
.pdp-loading__badge,
.pdp-loading__brand,
.pdp-loading__name,
.pdp-loading__star,
.pdp-loading__price,
.pdp-loading__compare,
.pdp-loading__size,
.pdp-loading__button,
.pdp-loading__wishlist,
.pdp-loading__notice,
.pdp-loading__chevron {
  display: block;
  background: linear-gradient(
    90deg,
    var(--color-bg) 25%,
    var(--color-border) 50%,
    var(--color-bg) 75%
  );
  background-size: 200% 100%;
  animation: pdp-loading-shimmer 1.4s ease-in-out infinite;
}

.pdp-loading__line {
  height: 12px;
}

.pdp-loading__dot {
  width: 4px;
  height: 4px;
  border-radius: 999px;
}

.pdp-loading__line--crumb {
  width: 90px;
}

.pdp-loading__line--crumb-wide {
  width: 120px;
}

.pdp-loading__line--crumb-current {
  width: 150px;
}

.pdp-loading__image {
  aspect-ratio: 3 / 4;
}

.pdp-loading__thumb {
  width: 72px;
  aspect-ratio: 3 / 4;
}

/* Katalog (proles): szkielet w kształcie galerii „duże zdjęcie + miniatury"
   (ProductGallery, BrandConfig.pdpGalleryLayout) — bez skoku po załadowaniu. */
.pdp-loading__gallery--main-thumbs {
  align-items: center;
}

.pdp-loading__gallery--main-thumbs .pdp-loading__image {
  width: min(100%, 72vh, 720px);
  aspect-ratio: 1;
}

.pdp-loading__gallery--main-thumbs .pdp-loading__thumb {
  aspect-ratio: 1;
}

.pdp-loading__badge {
  width: 62px;
  height: 28px;
  border-radius: 2px;
}

.pdp-loading__brand {
  width: 96px;
  height: 14px;
}

.pdp-loading__name {
  width: min(100%, 320px);
  height: 34px;
}

.pdp-loading__rating {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pdp-loading__star {
  width: 14px;
  height: 14px;
  clip-path: polygon(
    50% 0%,
    61% 35%,
    98% 35%,
    68% 57%,
    79% 91%,
    50% 70%,
    21% 91%,
    32% 57%,
    2% 35%,
    39% 35%
  );
}

.pdp-loading__line--reviews {
  width: 72px;
  margin-left: 4px;
}

.pdp-loading__price-block {
  display: grid;
  grid-template-columns: auto auto;
  gap: 8px 12px;
  align-items: end;
  width: min(100%, 300px);
}

.pdp-loading__price {
  width: 120px;
  height: 30px;
}

.pdp-loading__compare {
  width: 72px;
  height: 18px;
}

.pdp-loading__line--lowest-price {
  grid-column: 1 / -1;
  width: 230px;
}

.pdp-loading__divider {
  height: 1px;
  background: var(--color-border);
}

.pdp-loading__field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.pdp-loading__line--field-label {
  width: 130px;
}

.pdp-loading__line--availability {
  width: 150px;
  height: 22px;
  border-radius: 999px;
}

.pdp-loading__size {
  width: 56px;
  height: 40px;
}

.pdp-loading__line--size-chart {
  width: 116px;
}

.pdp-loading__cta {
  display: grid;
  grid-template-columns: 1fr 56px;
  gap: 8px;
}

.pdp-loading__button {
  width: 100%;
  height: 56px;
  border-radius: 999px;
}

.pdp-loading__wishlist {
  width: 56px;
  height: 56px;
  border-radius: 999px;
}

.pdp-loading__notices {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pdp-loading__notice {
  width: min(100%, 280px);
  height: 36px;
  border-radius: 999px;
}

.pdp-loading__notice--short {
  width: min(100%, 220px);
}

.pdp-loading__accordions {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--color-border);
}

.pdp-loading__accordion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  border-bottom: 1px solid var(--color-border);
}

.pdp-loading__line--accordion {
  width: min(70%, 220px);
}

.pdp-loading__chevron {
  width: 14px;
  height: 14px;
  clip-path: polygon(50% 70%, 12% 28%, 24% 16%, 50% 44%, 76% 16%, 88% 28%);
}

@keyframes pdp-loading-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

/* ── Breadcrumb ── */
.pdp__breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 20px;
  padding-bottom: 20px;
}

.pdp__breadcrumb-link {
  font-size: 0.75rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  line-height: 1;
  transition: color var(--transition-fast);
}

.pdp__breadcrumb-link:hover {
  color: var(--color-black);
}

.pdp__breadcrumb-sep {
  font-size: 0.75rem;
  color: var(--color-muted);
}

.pdp__breadcrumb-current {
  font-size: 0.75rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1;
  text-transform: uppercase;
}

/* ── Layout ── */
.pdp__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: 0;
  align-items: start;
  margin-bottom: 50px;
}

.pdp__media {
  position: relative;
  min-width: 0;
}

/* ── Panel ── */
.pdp__panel {
  padding: 0 0 40px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  position: sticky;
  top: 100px;
}

.pdp__promo-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 15px;
  border-radius: 9999px;
  background: var(--color-sale);
  color: var(--color-white);
  font-size: 0.75rem;
  font-weight: var(--font-weight-normal);
  line-height: 1;
  white-space: nowrap;
}

.pdp__promo-badge--media {
  display: inline-flex;
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
  pointer-events: none;
}

/* Badges */
.pdp__badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* Brand */
.pdp__brand {
  margin: 0;
  font-size: 0.75rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1;
}

.pdp__brand[href] {
  text-decoration: underline;
}

/* Name */
.pdp__name {
  margin: 0;
  font-size: 1.5rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1.2;
  text-transform: uppercase;
}

/* Rating */
.pdp__rating {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Price */
.pdp__price-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pdp__price-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.pdp__price {
  font-size: 1.5rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1;
}

.pdp__price--sale {
  color: var(--color-sale);
}

/* „+ VAT 23%" przy cenie (BrandConfig.priceTaxNote) — dopisek, nie druga cena. */
.pdp__price-tax {
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
}

.pdp__compare {
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  text-decoration: line-through;
}

.pdp__lowest {
  margin: 0;
  font-size: 0.75rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  line-height: 1;
}

/* Divider */
.pdp__divider {
  height: 1px;
  background: var(--color-border);
}

/* Fields */
.pdp__field {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pdp__field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.pdp__field-label {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1;
}

.pdp__field-value {
  font-weight: var(--font-weight-light);
}

/* Kolor i inne grupy atrybutów (sklep katalogowy) — ten sam rodzaj wyboru co
   rozmiar, więc wygląd przycisków z ProductSizeSelector. Nazwy kolorów są
   dłuższe od rozmiarów (32 wartości przy odzieży), stąd mniejsza czcionka. */
.pdp__variant-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pdp__variant-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  min-height: 40px;
  padding: 4px 12px;
  border: 0;
  border-bottom: 2px solid var(--color-border, #e0e0e0);
  border-radius: var(--size-option-radius);
  background: transparent;
  font-size: 0.9375rem;
  font-family: var(--font-body);
  font-weight: var(--font-weight-light);
  color: inherit;
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s;
}

.pdp__variant-btn:hover {
  border-color: var(--color-primary, #111);
}

.pdp__variant-btn--active {
  border-bottom-color: var(--color-primary, #111);
  color: var(--color-primary, #111);
  font-weight: var(--font-weight-medium);
}

.pdp__availability {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
}

.pdp__availability-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--color-muted);
}

.pdp__availability-dot--green {
  background: #1f7a3f;
}
.pdp__availability-dot--yellow {
  background: #8a6500;
}
.pdp__availability-dot--red {
  background: #b42318;
}

.pdp__size-guide-link {
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
}

/* Sizes */
.pdp__sizes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pdp__size-btn {
  min-width: 56px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--color-border-md);
  background: var(--color-white);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  cursor: pointer;
  transition:
    border-color var(--transition-fast),
    background var(--transition-fast);
}

.pdp__size-btn--active {
  border-color: var(--color-black);
  background: var(--color-black);
  color: var(--color-white);
}

.pdp__size-btn--unavailable {
  opacity: 0.3;
  cursor: not-allowed;
  text-decoration: line-through;
}

/* CTA */
.pdp__cta-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pdp__stock-error {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-sale);
}

.pdp__stock-limit {
  margin: 8px 0 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-sale);
}

.pdp__cta {
  display: flex;
  gap: 8px;
}

/* Sklep katalogowy ma w tym rzędzie wycenę i listę zapytania (ulubionych nie
   ma — wymagają konta). W kolumnie PDP (~360 px) „Zapytaj o wycenę" ściskało
   się przez `flex: 1 1 0%` do 83 px: etykieta łamała się na trzy linie
   i wychodziła poza pigułkę o sztywnej wysokości 56 px. Wycena dostaje więc
   własny rząd, a lista — drugi, na całą szerokość. */
.pdp__cta--quote {
  flex-wrap: wrap;
}

.pdp__cta--quote .pdp__add-to-cart {
  flex: 1 1 100%;
  padding: 0 20px;
}

.pdp__cta--quote > :not(.pdp__add-to-cart) {
  flex: 1 1 auto;
}

/* Lista zapytania jest komponentem współdzielonym z listingiem, gdzie ma
   własny, prostokątny kształt. Tutaj stoi obok pigułek 56 px, więc tylko
   w tym rzędzie dorównujemy jej wysokość i zaokrąglenie — bez ruszania
   wyglądu komponentu w pozostałych widokach. */
.pdp__cta--quote :deep(.quote-list-button__action) {
  min-height: 56px;
  border-radius: 999px;
}

@media (max-width: 960px) {
  .pdp__cta {
    display: none;
  }
}

.pdp__add-to-cart {
  flex: 1;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: var(--btn-primary-bg, var(--color-black));
  color: var(--btn-primary-text, var(--color-white));
  border: 1px solid var(--btn-primary-bg, var(--color-black));
  border-radius: var(--btn-pill-radius);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.04em;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast),
    border-color var(--transition-fast);
}

.pdp__add-to-cart:hover {
  background: var(--color-white);
  color: var(--color-black);
}

.pdp__add-to-cart:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pdp__wishlist-btn {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-white);
  border: 1px solid var(--color-border-md);
  border-radius: var(--btn-pill-radius);
  cursor: pointer;
  color: var(--color-black);
  transition:
    background var(--transition-fast),
    color var(--transition-fast),
    border-color var(--transition-fast);
}

.pdp__wishlist-btn:hover {
  background: var(--color-black);
  color: var(--color-white);
  border-color: var(--color-black);
}

.pdp__wishlist-btn.is-favorite {
  color: var(--color-sale);
}

/* Notices */
.pdp__notices {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Blok stałych kodów rabatowych (PromoStandingCodes) na karcie produktu */
.pdp__standing-codes {
  margin-top: 16px;
}

/* Pill carrier hours (na dole .pdp__cta-wrap) ma ten sam styl co powiadomienia
   promo — dociągnij notices do rytmu 8px (panel-gap to 20px), żeby oba bloki
   czytały się jako jeden ciągły stos pilli, a nie dwie sekcje. */
.pdp__cta-wrap:has(.pdp__notice--cta) + .pdp__notices {
  margin-top: -12px;
}

.pdp__notice {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 16px;
  background: var(--promo-pill-bg);
  border: var(--promo-pill-border);
  border-radius: 55px;
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--color-black);
  line-height: 1;
}

.pdp__notice strong {
  font-weight: 700;
}

/* Accordions */
.pdp__accordions {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--color-border);
}

.pdp__accordion {
  border-bottom: 1px solid var(--color-border);
}

.pdp__accordion-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  cursor: pointer;
  text-align: left;
}

.pdp__accordion-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.pdp__accordion-icon {
  flex-shrink: 0;
  color: var(--color-black);
  opacity: 0.75;
}

.pdp__accordion-body {
  padding: 0 0 16px;
  font-size: 14px;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1.6;
}

.pdp__accordion-body p {
  margin: 0;
}

.pdp__accordion-body p + p,
.pdp__accordion-body p + .pdp__accordion-list,
.pdp__accordion-body .pdp__accordion-list + p {
  margin-top: 10px;
}

.pdp__accordion-list {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
}

.pdp__accordion-list li {
  margin: 2px 0;
}

/* Kroki zapytania (tryb katalogowy) — numerowane, bo kolejność jest treścią. */
.pdp__process-list {
  list-style: decimal;
}

.pdp__process-list li + li {
  margin-top: 6px;
}

.pdp__accordion-link {
  text-decoration: underline;
}

.pdp__description {
  white-space: pre-line;
}

.pdp__info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
  margin: 0;
}

.pdp__info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.pdp__info-label {
  font-size: 0.875rem;
  color: var(--color-muted);
  font-weight: 400;
}

.pdp__info-value {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  line-height: 1.5;
}

.pdp__material {
  margin: 0;
  color: var(--color-muted);
}

/* ── Related ── */
.pdp__related {
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.pdp__related-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
}

.pdp__related-grid :deep(.product-card) {
  min-width: 0;
  overflow: hidden;
}

/* ── Responsive ── */
/* Katalog (proles): galeria „duże zdjęcie + miniatury" ma maks. 720 px, więc
   kolumna zdjęcia nie rozciąga się na całą szerokość — blok zdjęcie + panel
   stoi zwarty pośrodku (przy 1920 px było ~340 px pustki między nimi). */
@media (min-width: 1401px) {
  .pdp__layout--compact-media,
  .pdp-loading__layout--compact-media {
    grid-template-columns: minmax(0, 800px) 400px;
    justify-content: center;
  }
}

/* Katalog od 768 px: kwadrat zdjęcia jest wyśrodkowany i węższy od kolumny —
   badge rabatu liczony od krawędzi zdjęcia, nie kolumny (% = szer. .pdp__media). */
@media (min-width: 768px) {
  .pdp__layout--compact-media .pdp__promo-badge--media {
    left: calc((100% - min(100%, 72vh, 720px)) / 2 + 12px);
  }
}

/* ≤960 px „Nowość" jest wciągany -40px na dół zdjęcia; w układzie main-thumbs
   dół galerii to rząd miniatur (zasłaniałby pierwszą) — badge zostaje pod
   galerią. Poniżej 768 px karuzela, tam nakładanie na zdjęcie jest zamierzone. */
@media (min-width: 768px) and (max-width: 960px) {
  .pdp__layout--compact-media .pdp__badges {
    margin-top: 12px;
  }
}

@media (max-width: 1400px) {
  .pdp__layout {
    grid-template-columns: minmax(0, 1fr) 360px;
  }
}

@media (max-width: 1100px) {
  .pdp__layout {
    grid-template-columns: minmax(0, 1fr) 320px;
  }

  .pdp__panel {
    padding-left: 24px;
  }
}

@media (max-width: 960px) {
  .pdp-loading__layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .pdp-loading__panel {
    padding-left: 0;
    padding-top: 24px;
  }

  .pdp__breadcrumb {
    padding: 10px 0;
  }

  /* Pełna ścieżka z menu (pdpBreadcrumbNavPath) jest dłuższa — na telefonie
     zawija się zamiast wypychać stronę w bok. */
  .pdp__breadcrumb--trail {
    flex-wrap: wrap;
    row-gap: 6px;
  }

  .pdp__layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .pdp__panel {
    padding-left: 0;
    position: static;
  }

  .pdp__badges {
    margin-top: -40px;
    z-index: 2;
    position: relative;
  }

  /* Gdy nad dolną krawędzią zdjęcia jest ProductPromoOverlay (kod rabatowy /
     info o dostawie), nie wciągaj badge'a na zdjęcie — inaczej zasłania kod. */
  .pdp__badges--overlay-clear {
    margin-top: 12px;
  }

  .pdp__related-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pdp__notices {
    display: none;
  }

  .pdp__cta-wrap {
    display: none;
  }
}

@media (min-width: 961px) {
  .pdp__promo-overlay {
    display: none;
  }
}

@media (min-width: 961px) {
  .pdp__info-grid {
    gap: 12px 24px;
  }
}

@media (max-width: 640px) {
  .pdp__related-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
